import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface ScreenProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Элемент справа от заголовка */
  action?: ReactNode;
  /** Фиксированная область внизу (кнопка) — учитывает safe-area */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Обёртка экрана: safe-area сверху/снизу (Telegram viewport + env()), отступы, заголовок tight.
 * Каждый экран должен иметь состояния загрузки/пусто/ошибка (BLUEPRINT 8) — см. `States`.
 */
export function Screen({ title, subtitle, action, footer, children, className }: ScreenProps) {
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  useLayoutEffect(() => {
    const el = footerRef.current;
    if (!el) {
      setFooterHeight(0);
      return;
    }
    const measure = () => setFooterHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [footer]);

  return (
    <div
      className={cn('flex min-h-screen flex-col', className)}
      style={{
        paddingTop: 'var(--safe-top)',
        paddingLeft: 'var(--safe-left)',
        paddingRight: 'var(--safe-right)',
      }}
    >
      {title ? (
        <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div className="min-w-0">
            <h1 className="text-[28px] leading-tight font-bold tracking-tight">{title}</h1>
            {subtitle ? <p className="text-muted mt-1 text-base">{subtitle}</p> : null}
          </div>
          {action}
        </header>
      ) : null}
      <main
        className="flex flex-1 flex-col gap-4 px-4 pb-6"
        style={{
          paddingBottom: footer
            ? `${footerHeight + 16}px`
            : 'calc(var(--safe-bottom) + 1.5rem)',
        }}
      >
        {children}
      </main>
      {footer ? (
        <div
          ref={footerRef}
          className="chrome-bottom fixed inset-x-0 bottom-0 z-40 px-4 pt-3"
          style={{ paddingBottom: 'calc(var(--safe-bottom) + 1rem)' }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
